/**
 * ContractAlerts — waarschuwingen bij de contracten van één medewerker:
 * verstuurd maar na 7 dagen nog niet ondertekend, en de ketenregeling.
 *
 * Aflopende contracten staan hier niet meer: die zijn een beslispunt met
 * knoppen (ContractBeslisKaart, api/workforce/contract-einde). Een verstreken
 * einddatum is geen uitdienstdatum (Jorrit Hogenkamp stond 196 dagen rood
 * met "zet uit dienst" terwijl hij doorwerkte, 03-11-2026).
 *
 * De keten-telling is dezelfde functie als de API-poort (lib/contract-keten.ts).
 */

import type { Contract } from '../../stores/workforce-store';
import { ketenStand } from '../../lib/contract-keten';

interface ContractAlert {
  type: 'unsigned' | 'ketenregeling';
  detail: string;
  severity: 'warning' | 'info';
}

function differenceInDays(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
}

function vandaagISO(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
}

function getAlerts(contracts: Contract[]): ContractAlert[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const alerts: ContractAlert[] = [];

  for (const c of contracts) {
    if (c.status === 'verstuurd' && c.sent_at) {
      const sentDaysAgo = differenceInDays(today, new Date(c.sent_at));
      if (sentDaysAgo >= 7) {
        alerts.push({
          type: 'unsigned',
          detail: `${c.contract_number} is ${sentDaysAgo} dagen geleden verstuurd maar nog niet ondertekend`,
          severity: 'warning',
        });
      }
    }
  }

  const keten = ketenStand(contracts, vandaagISO());
  if (keten.volgendeMoetOnbepaald) {
    alerts.push({
      type: 'ketenregeling',
      detail: `Ketenregeling: ${keten.reden}. Een nieuw contract voor bepaalde tijd wordt geweigerd.`,
      severity: 'warning',
    });
  } else if (keten.aantal === 2) {
    alerts.push({
      type: 'ketenregeling',
      detail: `Ketenregeling: 2 contracten voor bepaalde tijd in de keten (${keten.schakels.join(', ')}). Het volgende mag nog bepaald zijn, het vierde niet meer.`,
      severity: 'info',
    });
  }

  return alerts;
}

const severityStyles = {
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
};

const severityIconBg = {
  warning: 'bg-amber-100 text-amber-600',
  info: 'bg-blue-100 text-blue-600',
};

interface ContractAlertsProps {
  contracts: Contract[];
}

export function ContractAlerts({ contracts }: ContractAlertsProps) {
  const alerts = getAlerts(contracts);

  if (alerts.length === 0) return null;

  return (
    <div className="space-y-2">
      {alerts.map((alert, i) => (
        <div
          key={`${alert.type}-${i}`}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${severityStyles[alert.severity]}`}
        >
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${severityIconBg[alert.severity]}`}>
            !
          </span>
          <span className="text-sm">{alert.detail}</span>
        </div>
      ))}
    </div>
  );
}
